import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtCurrency } from "@/lib/format";
import { StickyNote, Bell, Trash2, Plus } from "lucide-react";

export function NotesCard({ ticker }) {
    const qc = useQueryClient();
    const [note, setNote] = useState("");
    const [dirty, setDirty] = useState(false);
    const q = useQuery({ queryKey: ["note", ticker], queryFn: async () => (await api.get(`/notes/${ticker}`)).data });
    useEffect(() => { if (!dirty && q.data) setNote(q.data.note || ""); }, [q.data, dirty]);

    const save = async () => {
        try {
            await api.put(`/notes/${ticker}`, { note });
            toast.success("Note saved");
            setDirty(false);
            qc.invalidateQueries({ queryKey: ["note", ticker] });
            qc.invalidateQueries({ queryKey: ["notes"] });
        } catch (e) { toast.error(apiErr(e)); }
    };

    return (
        <Card className="p-5" data-testid="notes-card">
            <div className="flex items-center gap-2 mb-3"><StickyNote className="w-5 h-5 text-primary" /><h2 className="font-heading text-lg font-semibold">Note</h2></div>
            <Textarea rows={3} value={note} onChange={(e) => { setNote(e.target.value); setDirty(true); }}
                placeholder={`e.g. "Buy more under $150" or a thesis for ${ticker}…`} data-testid="note-input" />
            <div className="flex justify-end mt-2">
                <Button size="sm" onClick={save} disabled={!dirty} data-testid="note-save-btn">Save note</Button>
            </div>
        </Card>
    );
}

const DIR = { above: "at or above", below: "at or below" };

export function AlertsCard({ ticker, currentPrice }) {
    const qc = useQueryClient();
    const [target, setTarget] = useState("");
    const [direction, setDirection] = useState("above");
    const [saving, setSaving] = useState(false);
    const q = useQuery({ queryKey: ["alerts"], queryFn: async () => (await api.get("/alerts")).data });
    const mine = (q.data?.alerts || []).filter((a) => a.ticker === ticker.toUpperCase());

    // pre-select direction based on the entered target vs current price
    useEffect(() => {
        const t = parseFloat(target);
        if (!isNaN(t) && currentPrice != null) setDirection(t >= currentPrice ? "above" : "below");
    }, [target, currentPrice]);

    const add = async () => {
        const t = parseFloat(target);
        if (isNaN(t) || t <= 0) return toast.error("Enter a valid target price");
        setSaving(true);
        try {
            await api.post("/alerts", { ticker: ticker.toUpperCase(), target_price: t, direction });
            toast.success("Alert set");
            setTarget("");
            qc.invalidateQueries({ queryKey: ["alerts"] });
        } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
    };
    const patch = async (id, action) => { await api.patch(`/alerts/${id}`, { action }); qc.invalidateQueries({ queryKey: ["alerts"] }); };
    const del = async (id) => { await api.delete(`/alerts/${id}`); qc.invalidateQueries({ queryKey: ["alerts"] }); };

    return (
        <Card className="p-5" data-testid="alerts-card">
            <div className="flex items-center gap-2 mb-3"><Bell className="w-5 h-5 text-primary" /><h2 className="font-heading text-lg font-semibold">Price Alerts</h2></div>
            <div className="flex items-end gap-2 mb-4">
                <div className="space-y-1.5 flex-1">
                    <Label className="text-xs">Target price</Label>
                    <Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="0.00" className="num" data-testid="alert-target-input" />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs">When price is</Label>
                    <Select value={direction} onValueChange={setDirection}>
                        <SelectTrigger className="w-[130px]" data-testid="alert-direction-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="above">At/above</SelectItem>
                            <SelectItem value="below">At/below</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Button onClick={add} disabled={saving} data-testid="alert-add-btn"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-2">
                {mine.length === 0 && <p className="text-sm text-muted-foreground" data-testid="alerts-card-empty">No alerts for {ticker.toUpperCase()} yet.</p>}
                {mine.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 text-sm py-1.5 border-b border-border/50" data-testid={`alert-item-${a.id}`}>
                        <div className="min-w-0">
                            <span className="num">{DIR[a.direction]} {fmtCurrency(a.target_price, { decimals: 2 })}</span>
                            <span className="text-xs text-muted-foreground ml-2">now {a.current_price != null ? fmtCurrency(a.current_price, { decimals: 2 }) : "—"}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            {a.status === "triggered" && <Badge className="bg-loss text-white text-[10px]">Triggered</Badge>}
                            {a.status === "dismissed" && <Badge variant="secondary" className="text-[10px]">Dismissed</Badge>}
                            {a.status === "active" && <Badge variant="outline" className="text-[10px]">Active</Badge>}
                            {a.status !== "active" && <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => patch(a.id, "rearm")} data-testid={`alert-rearm-${a.id}`}>Re-arm</Button>}
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-loss" onClick={() => del(a.id)} data-testid={`alert-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
}
